import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLocation, Link } from 'react-router-dom';
import { filtrarMenu } from '../config/menuModulos';
import { 
  Home, 
  Building2, 
  Truck, 
  Package, 
  FileText, 
  ClipboardCheck, 
  ShoppingCart, 
  Users, 
  Settings,
  BarChart3,
  X,
  Building,
  FileCheck,
  ChevronDown,
  ChevronRight,
  FolderOpen,
  Activity,
  Target,
  Factory,
  Calendar,
  Scale,
  AlertTriangle,
  MessageSquareWarning,
  Boxes,
  Box,
  Wrench,
  SlidersHorizontal,
  Tags,
  Sparkles,
  Cog,
  Warehouse,
  Database,
  Layers,
} from 'lucide-react';

/** Submenu reutilizado: Insumos (ingredientes, embalagens, serviços, limpeza) */
const insumosSubmenuItems = [
  { name: 'Ingredientes', href: '/app/insumos/ingredientes', icon: Package },
  { name: 'Embalagens', href: '/app/insumos/embalagens', icon: Box },
  { name: 'Serviços', href: '/app/insumos/servicos', icon: Wrench },
  { name: 'Limpeza e conservação', href: '/app/insumos/limpeza-conservacao', icon: Sparkles },
];

/** Mesma estrutura de Insumos: fornecedores por tipo de catálogo */
const fornecedoresSubmenuItems = [
  { name: 'Ingredientes', href: '/app/fornecedores/ingredientes', icon: Package },
  { name: 'Embalagens', href: '/app/fornecedores/embalagens', icon: Box },
  { name: 'Serviços', href: '/app/fornecedores/servicos', icon: Wrench },
  { name: 'Limpeza e conservação', href: '/app/fornecedores/limpeza-conservacao', icon: Sparkles },
  { name: 'Não classificados', href: '/app/fornecedores/nao-classificado', icon: AlertTriangle },
];

const producaoSubmenuItems = [
  { name: 'Planejamento', href: '/app/planejamento-producao', icon: Calendar },
  { name: 'Ordens de Produção', href: '/app/producao', icon: Factory },
  { name: 'Separação e Pesagem', href: '/app/separacao-pesagem', icon: Scale },
  { name: 'Fluxo por Setor', href: '/app/fluxo-setorial', icon: Boxes },
  { name: 'Relatórios de Produção', href: '/app/relatorios-producao', icon: FileText },
  { name: 'Não Conformidades', href: '/app/nao-conformidades-producao', icon: AlertTriangle },
];

const estoqueMenuItem = {
  name: 'Estoque',
  icon: Package,
  type: 'submenu',
  modulo: 'estoque',
  submenu: [
    { name: 'Saldo', href: '/app/estoque', icon: Package, modulo: 'estoque' },
    { name: 'Armazenamento', href: '/app/armazenamento', icon: Warehouse, modulo: 'armazenamento' },
  ],
};

const recebimentosMenuItem = {
  name: 'Recebimentos',
  icon: ClipboardCheck,
  type: 'submenu',
  modulo: 'recebimentos',
  submenu: [
    { name: 'Recebimentos', href: '/app/receivings', icon: ClipboardCheck, modulo: 'recebimentos' },
    { name: 'Operação de armazém', href: '/app/armazem-operacao', icon: Warehouse, modulo: 'operacao_armazem' },
  ],
};

const comprasMenuItem = {
  name: 'Compras',
  icon: ShoppingCart,
  type: 'submenu',
  modulo: 'compras',
  submenu: [
    { name: 'Cotações', href: '/app/cotacoes', icon: ShoppingCart, modulo: 'compras' },
  ],
};

const monitoramentoMenuItem = {
  name: 'Monitoramento',
  href: '/app/monitoramento',
  icon: Activity,
  modulo: 'monitoramento',
};

const expedicaoMenuItem = {
  name: 'Expedição',
  href: '/app/expedicao',
  icon: Truck,
  modulo: 'expedicao',
};

const equipamentosSubmenuItems = [
  { name: 'Painel', href: '/app/equipamentos/painel', icon: BarChart3 },
  { name: 'Equipamentos', href: '/app/equipamentos', icon: Cog },
  { name: 'Plano de Manutenção', href: '/app/plano-manutencao', icon: Wrench },
  { name: 'Cotações', href: '/app/equipamentos/cotacoes', icon: ShoppingCart },
];

const Sidebar = ({ onClose }) => {
  const { user, userData, isRole, companyModulos } = useAuth();
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState({});

  useEffect(() => {
    const produPaths = [
      '/app/producao',
      '/app/planejamento-producao',
      '/app/separacao-pesagem',
      '/app/fluxo-setorial',
      '/app/relatorios-producao',
      '/app/nao-conformidades-producao'
    ];
    const insumosPaths = [
      '/app/insumos',
      '/app/insumos/ingredientes',
      '/app/insumos/materias-primas',
      '/app/insumos/embalagens',
      '/app/insumos/servicos',
      '/app/insumos/limpeza-conservacao',
    ];
    const fornecedoresPaths = [
      '/app/fornecedores/ingredientes',
      '/app/fornecedores/embalagens',
      '/app/fornecedores/servicos',
      '/app/fornecedores/limpeza-conservacao',
      '/app/fornecedores/nao-classificado',
    ];
    const paramPaths = ['/app/parametros/categorias-insumos', '/app/parametros/tipos-equipamentos'];
    const equipPaths = ['/app/equipamentos', '/app/equipamentos/painel', '/app/equipamentos/cotacoes', '/app/plano-manutencao'];
    if (produPaths.some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`))) {
      setExpandedMenus((prev) => ({ ...prev, Produção: true }));
    }
    if (insumosPaths.some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`))) {
      setExpandedMenus((prev) => ({ ...prev, Insumos: true }));
    }
    if (fornecedoresPaths.some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`))) {
      setExpandedMenus((prev) => ({ ...prev, Fornecedores: true }));
    }
    if (paramPaths.some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`))) {
      setExpandedMenus((prev) => ({ ...prev, Parâmetros: true }));
    }
    if (equipPaths.some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`))) {
      setExpandedMenus((prev) => ({ ...prev, Equipamentos: true }));
    }
    if (['/app/estoque', '/app/armazenamento'].some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`))) {
      setExpandedMenus((prev) => ({ ...prev, Estoque: true }));
    }
    if (['/app/receivings', '/app/armazem-operacao'].some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`))) {
      setExpandedMenus((prev) => ({ ...prev, Recebimentos: true }));
    }
    if (location.pathname.startsWith('/app/cotacoes')) {
      setExpandedMenus((prev) => ({ ...prev, Compras: true }));
    }
  }, [location.pathname]);

  const getMenuItems = () => {
    const baseItems = [
      {
        name: 'Dashboard',
        href: '/app/dashboard',
        icon: Home,
        roles: [
          'superadmin',
          'gerente',
          'administrador',
          'admin',
          'recebedor',
          'fornecedor',
          'manutencao',
          'fornecedor_equipamentos',
          'producao',
          'qualidade',
        ],
      },
    ];

    const roleSpecificItems = {
      superadmin: [
        // SUPERADMIN - MENU REORGANIZADO COM SUBMENUS
        { name: 'Leads', href: '/app/leads', icon: Target },
        { name: 'Solicitações', href: '/app/solicitacoes-sistema', icon: MessageSquareWarning },
        { name: 'Painel de Migração', href: '/app/painel-migracao', icon: Database },
        { name: 'Arquitetura', href: '/app/arquitetura', icon: Layers },
        {
          name: 'Cadastros',
          icon: FolderOpen,
          type: 'submenu',
          submenu: [
            { name: 'Empresas', href: '/app/companies', icon: Building },
            { name: 'Produtos', href: '/app/produtos-finais', icon: Target },
            { name: 'Documentos Obrigatórios', href: '/app/documentos-obrigatorios', icon: FileCheck },
            { name: 'Usuários', href: '/app/users', icon: Users }
          ]
        },
        {
          name: 'Fornecedores',
          icon: Truck,
          type: 'submenu',
          submenu: fornecedoresSubmenuItems,
        },
        {
          name: 'Insumos',
          icon: Boxes,
          type: 'submenu',
          submenu: insumosSubmenuItems,
        },
        {
          name: 'Parâmetros',
          icon: SlidersHorizontal,
          type: 'submenu',
          submenu: [
            {
              name: 'Categorias de Insumos',
              href: '/app/parametros/categorias-insumos',
              icon: Tags,
            },
            {
              name: 'Tipos de Equipamentos',
              href: '/app/parametros/tipos-equipamentos',
              icon: Cog,
            },
          ],
        },
        {
          name: 'Equipamentos',
          icon: Cog,
          type: 'submenu',
          submenu: equipamentosSubmenuItems,
        },
        { name: 'Setores', href: '/app/setores', icon: Building2 },
        {
          name: 'Operação',
          icon: Activity,
          type: 'submenu',
          submenu: [
            { name: 'Documentos', href: '/app/documents', icon: FileText },
            { name: 'Estoque', href: '/app/estoque', icon: Package },
            { name: 'Armazenamento', href: '/app/armazenamento', icon: Warehouse },
            { name: 'Recebimentos', href: '/app/receivings', icon: ClipboardCheck },
            { name: 'Operação de armazém', href: '/app/armazem-operacao', icon: Warehouse },
            { name: 'Monitoramento', href: '/app/monitoramento', icon: Activity },
            { name: 'Expedição', href: '/app/expedicao', icon: Truck },
            { name: 'Marketplace', href: '/app/marketplace', icon: ShoppingCart }
          ]
        },
        {
          name: 'Produção',
          icon: Factory,
          type: 'submenu',
          submenu: producaoSubmenuItems
        },
        { name: 'Relatórios', href: '/app/reports', icon: BarChart3 },
        { name: 'Configurações', href: '/app/settings', icon: Settings }
      ],
      gerente: [
        {
          name: 'Produção',
          icon: Factory,
          type: 'submenu',
          submenu: producaoSubmenuItems
        },
        {
          name: 'Equipamentos',
          icon: Cog,
          type: 'submenu',
          submenu: equipamentosSubmenuItems,
        },
        { name: 'Setores', href: '/app/setores', icon: Building2 },
        {
          name: 'Fornecedores',
          icon: Truck,
          type: 'submenu',
          submenu: fornecedoresSubmenuItems,
        },
        {
          name: 'Insumos',
          icon: Boxes,
          type: 'submenu',
          submenu: insumosSubmenuItems,
        },
        { name: 'Produtos', href: '/app/produtos-finais', icon: Target },
        estoqueMenuItem,
        { name: 'Rastreabilidade', href: '/app/rastreabilidade', icon: Activity },
        comprasMenuItem,
        { name: 'Documentos', href: '/app/documents', icon: FileText },
        recebimentosMenuItem,
        monitoramentoMenuItem,
        expedicaoMenuItem,
        { name: 'Marketplace', href: '/app/marketplace', icon: ShoppingCart },
        { name: 'Usuários', href: '/app/users', icon: Users },
        { name: 'Relatórios', href: '/app/reports', icon: BarChart3 },
        { name: 'Configurações', href: '/app/settings', icon: Settings }
      ],
      administrador: [
        {
          name: 'Produção',
          icon: Factory,
          type: 'submenu',
          submenu: producaoSubmenuItems
        },
        {
          name: 'Equipamentos',
          icon: Cog,
          type: 'submenu',
          submenu: equipamentosSubmenuItems,
        },
        { name: 'Setores', href: '/app/setores', icon: Building2 },
        {
          name: 'Fornecedores',
          icon: Truck,
          type: 'submenu',
          submenu: fornecedoresSubmenuItems,
        },
        {
          name: 'Insumos',
          icon: Boxes,
          type: 'submenu',
          submenu: insumosSubmenuItems,
        },
        { name: 'Produtos', href: '/app/produtos-finais', icon: Target },
        estoqueMenuItem,
        { name: 'Rastreabilidade', href: '/app/rastreabilidade', icon: Activity },
        comprasMenuItem,
        { name: 'Documentos', href: '/app/documents', icon: FileText },
        recebimentosMenuItem,
        monitoramentoMenuItem,
        expedicaoMenuItem,
        { name: 'Usuários', href: '/app/users', icon: Users },
        { name: 'Relatórios', href: '/app/reports', icon: BarChart3 },
        { name: 'Configurações', href: '/app/settings', icon: Settings }
      ],
      admin: [ // ✅ CORREÇÃO: Adicionar suporte para 'admin'
        {
          name: 'Produção',
          icon: Factory,
          type: 'submenu',
          submenu: producaoSubmenuItems
        },
        {
          name: 'Equipamentos',
          icon: Cog,
          type: 'submenu',
          submenu: equipamentosSubmenuItems,
        },
        { name: 'Setores', href: '/app/setores', icon: Building2 },
        {
          name: 'Fornecedores',
          icon: Truck,
          type: 'submenu',
          submenu: fornecedoresSubmenuItems,
        },
        {
          name: 'Insumos',
          icon: Boxes,
          type: 'submenu',
          submenu: insumosSubmenuItems,
        },
        { name: 'Produtos', href: '/app/produtos-finais', icon: Target },
        estoqueMenuItem,
        { name: 'Rastreabilidade', href: '/app/rastreabilidade', icon: Activity },
        comprasMenuItem,
        { name: 'Documentos', href: '/app/documents', icon: FileText },
        recebimentosMenuItem,
        monitoramentoMenuItem,
        expedicaoMenuItem,
        { name: 'Usuários', href: '/app/users', icon: Users },
        { name: 'Relatórios', href: '/app/reports', icon: BarChart3 },
        { name: 'Configurações', href: '/app/settings', icon: Settings }
      ],
      recebedor: [
        recebimentosMenuItem,
        monitoramentoMenuItem,
        {
          name: 'Fornecedores',
          icon: Truck,
          type: 'submenu',
          submenu: fornecedoresSubmenuItems,
        },
        {
          name: 'Insumos',
          icon: Boxes,
          type: 'submenu',
          submenu: insumosSubmenuItems,
        },
      ],
      qualidade: [
        {
          name: 'Produção',
          icon: Factory,
          type: 'submenu',
          submenu: [
            { name: 'Fluxo por Setor', href: '/app/fluxo-setorial', icon: Boxes },
            { name: 'Relatórios de Produção', href: '/app/relatorios-producao', icon: FileText },
            { name: 'Não Conformidades', href: '/app/nao-conformidades-producao', icon: AlertTriangle }
          ]
        },
        recebimentosMenuItem,
        estoqueMenuItem,
        { name: 'Rastreabilidade', href: '/app/rastreabilidade', icon: Activity },
        monitoramentoMenuItem,
        { name: 'Documentos', href: '/app/documents', icon: FileText },
        {
          name: 'Fornecedores',
          icon: Truck,
          type: 'submenu',
          submenu: fornecedoresSubmenuItems,
        },
        {
          name: 'Insumos',
          icon: Boxes,
          type: 'submenu',
          submenu: insumosSubmenuItems,
        },
      ],
      manutencao: [
        {
          name: 'Equipamentos',
          icon: Cog,
          type: 'submenu',
          submenu: equipamentosSubmenuItems,
        },
      ],
      fornecedor_equipamentos: [
        { name: 'Marketplace Equipamentos', href: '/app/fornecedor-equipamentos', icon: Cog },
      ],
      producao: [ // ✅ NOVO PERFIL: Produção
        {
          name: 'Produção',
          icon: Factory,
          type: 'submenu',
          submenu: producaoSubmenuItems
        },
        { name: 'Produtos', href: '/app/produtos-finais', icon: Package },
        estoqueMenuItem,
        monitoramentoMenuItem,
        {
          name: 'Insumos',
          icon: Boxes,
          type: 'submenu',
          submenu: insumosSubmenuItems,
        },
      ],
      fornecedor: [
        comprasMenuItem,
        {
          name: 'Insumos',
          icon: Boxes,
          type: 'submenu',
          submenu: insumosSubmenuItems,
        },
        { name: 'Documentos Obrigatórios', href: '/app/fornecedor-documentos', icon: FileText }, // ✅ NOVA: Documentos com status e alertas
        { name: 'Recebimentos', href: '/app/receivings', icon: ClipboardCheck },
        { name: 'Marketplace', href: '/app/marketplace', icon: ShoppingCart },
        { name: 'Minhas Propostas', href: '/app/propostas', icon: ClipboardCheck }
      ]
    };

    const porNome = {
      Produção: 'producao',
      Equipamentos: 'equipamentos',
      Fornecedores: 'fornecedores',
      Insumos: 'insumos',
      Parâmetros: 'parametros',
      Setores: 'setores',
      Produtos: 'produtos',
      Documentos: 'documentos',
      'Documentos Obrigatórios': 'documentos',
      Usuários: 'usuarios',
      Relatórios: 'relatorios',
      Configurações: 'configuracoes',
      Marketplace: 'marketplace',
      Rastreabilidade: 'rastreabilidade',
    };
    const porHref = {
      '/app/setores': 'setores',
      '/app/produtos-finais': 'produtos',
      '/app/documents': 'documentos',
      '/app/fornecedor-documentos': 'documentos',
      '/app/documentos-obrigatorios': 'documentos',
      '/app/users': 'usuarios',
      '/app/reports': 'relatorios',
      '/app/settings': 'configuracoes',
      '/app/marketplace': 'marketplace',
      '/app/propostas': 'marketplace',
      '/app/rastreabilidade': 'rastreabilidade',
      '/app/fornecedor-equipamentos': 'equipamentos',
      '/app/cotacoes': 'compras',
      '/app/estoque': 'estoque',
      '/app/receivings': 'recebimentos',
      '/app/armazem-operacao': 'operacao_armazem',
      '/app/armazenamento': 'armazenamento',
    };
    const marcar = (item) => {
      const modulo = item.modulo || porNome[item.name] || porHref[item.href];
      const submenu = Array.isArray(item.submenu) ? item.submenu.map(marcar) : undefined;
      return {
        ...item,
        ...(modulo ? { modulo } : {}),
        ...(submenu ? { submenu } : {}),
      };
    };

    const userRole = userData?.funcao;
    const specificItems = (roleSpecificItems[userRole] || []).map(marcar);
    const bruto = [...baseItems, ...specificItems.map(item => ({ ...item, roles: [userRole] }))];
    if (userRole === 'superadmin' || companyModulos === null) return bruto;
    if (companyModulos === undefined) return baseItems;
    return filtrarMenu(bruto, companyModulos);
  };

  const menuItems = getMenuItems();

  const isActive = (href) => {
    // Verifica se a rota atual é exatamente o href ou começa com o href (para rotas aninhadas)
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  const toggleSubmenu = (menuName) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuName]: !prev[menuName]
    }));
  };

  const isSubmenuActive = (submenu) => {
    return submenu.some(item => isActive(item.href));
  };

  return (
    <div className="flex h-full flex-col bg-gradient-to-b from-blue-900 via-blue-800 to-purple-900">
      {/* Header with Logo only - sem texto redundante */}
      <div className="flex h-16 shrink-0 items-center justify-between px-6 border-b border-blue-700/50">
        <div className="flex items-center">
          <img 
            src="/logo-qualimanager-horizontal.jpeg" 
            alt="QualiManager" 
            className="h-10 w-auto max-w-[180px] bg-white rounded px-2 py-1 object-contain"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div className="hidden w-8 h-8 bg-white rounded-lg items-center justify-center">
            <span className="text-blue-900 font-bold text-lg">Q</span>
          </div>
        </div>
        {onClose && (
          <button
            type="button"
            className="lg:hidden text-white hover:text-blue-200 p-1"
            onClick={onClose}
          >
            <X className="h-6 w-6" />
          </button>
        )}
      </div>

      {/* User info */}
      <div className="px-6 py-4 border-b border-blue-700/50">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <span className="text-white text-sm font-medium">
              {userData?.nome?.charAt(0)?.toUpperCase() || user?.displayName?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase()}
            </span>
          </div>
          <div>
            <p className="text-white text-sm font-medium truncate">
              {userData?.nome || user?.displayName || user?.email}
            </p>
            <p className="text-blue-200 text-xs">
              {(userData?.funcao || user?.funcao) === 'superadmin' && 'Super Administrador'}
              {(userData?.funcao || user?.funcao) === 'gerente' && 'Gerente'}
              {(userData?.funcao || user?.funcao) === 'administrador' && 'Administrador'}
              {(userData?.funcao || user?.funcao) === 'recebedor' && 'Recebedor'}
              {(userData?.funcao || user?.funcao) === 'fornecedor' && 'Fornecedor'}
            </p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
        {menuItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.href);
          
          // Se é um submenu
          if (item.type === 'submenu') {
            const isExpanded = expandedMenus[item.name];
            const submenuActive = isSubmenuActive(item.submenu);
            
            return (
              <div key={item.name} className="space-y-1">
                <button
                  onClick={() => toggleSubmenu(item.name)}
                  className={`
                    group w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200
                    ${submenuActive 
                      ? 'bg-white/20 text-white shadow-lg backdrop-blur-sm' 
                      : 'text-blue-100 hover:bg-white/10 hover:text-white'
                    }
                  `}
                >
                  <div className="flex items-center">
                    <Icon className={`
                      mr-3 h-5 w-5 transition-colors duration-200
                      ${submenuActive ? 'text-white' : 'text-blue-200 group-hover:text-white'}
                    `} />
                    {item.name}
                  </div>
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4 text-blue-200 group-hover:text-white" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-blue-200 group-hover:text-white" />
                  )}
                </button>
                
                {/* Submenu items */}
                {isExpanded && (
                  <div className="ml-6 space-y-1">
                    {item.submenu.map((subItem) => {
                      const SubIcon = subItem.icon;
                      const subActive = isActive(subItem.href);
                      
                      return (
                        <Link
                          key={subItem.name}
                          to={subItem.href}
                          onClick={onClose}
                          className={`
                            group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200
                            ${subActive 
                              ? 'bg-white/20 text-white shadow-lg backdrop-blur-sm' 
                              : 'text-blue-100 hover:bg-white/10 hover:text-white'
                            }
                          `}
                        >
                          <SubIcon className={`
                            mr-3 h-4 w-4 transition-colors duration-200
                            ${subActive ? 'text-white' : 'text-blue-200 group-hover:text-white'}
                          `} />
                          {subItem.name}
                          {subActive && (
                            <div className="ml-auto w-2 h-2 bg-white rounded-full"></div>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }
          
          // Menu item normal
          return (
            <Link
              key={item.name}
              to={item.href}
              onClick={onClose}
              className={`
                group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200
                ${active 
                  ? 'bg-white/20 text-white shadow-lg backdrop-blur-sm' 
                  : 'text-blue-100 hover:bg-white/10 hover:text-white'
                }
              `}
            >
              <Icon className={`
                mr-3 h-5 w-5 transition-colors duration-200
                ${active ? 'text-white' : 'text-blue-200 group-hover:text-white'}
              `} />
              {item.name}
              {active && (
                <div className="ml-auto w-2 h-2 bg-white rounded-full"></div>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-blue-700/50">
        <div className="text-center">
          <p className="text-blue-200 text-xs">
            © {new Date().getFullYear()} QualiManager
          </p>
          <p className="text-blue-300 text-xs mt-1">
            Gestão que aprova.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;

